/**
 * Utilitário para exibição de status de entrega de mensagens (email, WhatsApp, etc.).
 * Consome o objeto deliveryStatus retornado pela API (já parseado no backend).
 *
 * Uso: getDeliveryStatusDisplay(message) => { label, icon, errorReason, isPending, isError }
 */

/** Mapeamento status WhatsApp (numérico) para chave de ícone */
const WHATSAPP_STATUS_ICON = {
  0: "pending",   // Enviando
  1: "sent",      // Enviado
  2: "delivered", // Entregue
  3: "read",      // Lido
  4: "error",     // Falha
};

/**
 * Retorna dados para exibir o status de entrega na UI.
 * @param {object} message - mensagem com deliveryStatus (e opcionalmente updatedAt para “Enviando”)
 * @returns {{ label: string | null, icon: string | null, errorReason: string | null, isPending: boolean, isError: boolean }}
 */
export function getDeliveryStatusDisplay(message) {
  const ds = message?.deliveryStatus;
  if (!ds || (ds.label == null && ds.errorReason == null)) {
    return { label: null, icon: null, errorReason: null, isPending: false, isError: false };
  }

  const isError = ds.status === 4 || ds.status === "bounced" || ds.status === "dropped" || ds.status === "complaint" || !!ds.errorReason;
  const isPending = ds.status === 0 || ds.status === "sending";

  let icon = null;
  if (typeof ds.status === "number" && WHATSAPP_STATUS_ICON[ds.status]) {
    icon = WHATSAPP_STATUS_ICON[ds.status];
  } else if (ds.status === "sent") {
    icon = "sent";
  } else if (ds.status === "delivered" || ds.status === "opened" || ds.status === "clicked") {
    icon = "delivered";
  } else if (ds.status === "bounced" || ds.status === "dropped" || ds.status === "complaint") {
    icon = "error";
  }

  return {
    label: ds.label || null,
    icon,
    errorReason: ds.errorReason || null,
    isPending,
    isError,
  };
}

/**
 * Converte mensagens técnicas de erro do provedor em textos mais amigáveis
 * para o atendente, explicando o que aconteceu e como resolver.
 */
export function getFriendlyDeliveryError(errorReason) {
  if (!errorReason) return null;
  const raw = String(errorReason).trim();
  if (!raw) return null;

  const lower = raw.toLowerCase();

  // Erro típico da Meta: quantidade de parâmetros diferente do esperado no template.
  if (lower.includes("oauthexception") && lower.includes("number of parameters does not match")) {
    return (
      "O WhatsApp rejeitou esta mensagem porque o template configurado na Meta " +
      "está com uma quantidade de campos diferente da que foi enviada. " +
      "Peça ao responsável pelo canal para revisar o template na Meta/XPRO " +
      "e garantir que o número de variáveis e a ordem sejam os mesmos."
    );
  }

  // Erro típico da Gupshup: texto não corresponde ao template aprovado.
  if (lower.includes("message sending failed as template did not match") ||
      (lower.includes("template") && lower.includes("did not match"))) {
    return (
      "O envio falhou porque o conteúdo não corresponde ao modelo aprovado pelo WhatsApp. " +
      "Verifique se você escolheu o template correto e preencheu todos os campos obrigatórios " +
      "(placeholders) antes de reenviar."
    );
  }

  // Fallback: devolve o texto original (já tratado em outros pontos da UI).
  return raw;
}

/**
 * Retorna se a mensagem deve exibir linha de status (apenas mensagens enviadas pelo agente com canal que tem status).
 */
export function shouldShowDeliveryStatus(message, isUser) {
  if (!isUser) return false;
  const ds = message?.deliveryStatus;
  if (!ds) return false;
  return ds.label != null || ds.errorReason != null;
}

/** Erros que NÃO são de janela 24h (ex.: 131049 = ecosystem); não exibir mensagem de reengajamento. */
const NOT_REENGAGEMENT = [
  /131049|#131049/i,
  /engajamento\s*saudável|healthy\s*ecosystem|ecosystem\s*engagement/i,
];

/** Indica se o erro é de janela de 24h do WhatsApp (re-engagement / código 470 ou 131042). */
const REENGAGEMENT_PATTERNS = [
  /re-engagement/i,
  /#470\b/i,
  /\(\s*470\s*\)/i,
  /#131042\b/i,
  /(janela|window).*(24\s*h|24\s*hour).*(fechada|expirada|expirou|closed|expired)/i,
  /(fora da janela|outside.*window|24\s*hour.*window)/i,
];

/**
 * Verifica se o errorReason indica erro de reengajamento (janela de 24h fechada).
 * Erro 131049 (healthy ecosystem) não é reengajamento — exibir a mensagem real.
 * @param {string | null} errorReason
 * @returns {boolean}
 */
export function isReengagementError(errorReason) {
  if (errorReason == null || typeof errorReason !== "string") return false;
  const s = errorReason.trim();
  if (NOT_REENGAGEMENT.some((re) => re.test(s))) return false;
  return REENGAGEMENT_PATTERNS.some((re) => re.test(s));
}

/** Mensagem amigável para exibir quando o envio falhou por janela de 24h. */
export const REENGAGEMENT_FRIENDLY_MESSAGE =
  "Janela de 24h do WhatsApp fechada. Para responder, use um template de reengajamento no campo de mensagem ou aguarde o cliente enviar uma nova mensagem.";
